import React, { ButtonHTMLAttributes } from "react";
import { motion } from "framer-motion";
import { cn } from "../utils/cn";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "text" | "danger";
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  children?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "medium",
  fullWidth = false,
  icon,
  loading = false,
  className,
  children,
  disabled,
  ...props
}) => {
  const baseStyles =
    "relative inline-flex items-center justify-center rounded-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed";

  const variantStyles = {
    primary:
      "bg-gold hover:bg-gold-600 text-black focus:ring-gold shadow-glow-sm hover:shadow-glow-gold transition-colors",
    secondary: "bg-white/10 hover:bg-white/15 text-white focus:ring-white/25",
    outline:
      "bg-transparent border border-white/20 hover:bg-white/5 text-white focus:ring-white/25",
    text: "bg-transparent hover:bg-white/5 text-white focus:ring-white/25",
    danger: "bg-red-500 hover:bg-red-600 text-white focus:ring-red-500",
  };

  const sizeStyles = {
    small: "text-xs px-4 py-2 min-h-[36px]",
    medium: "text-sm px-5 py-2.5 min-h-[44px]",
    large: "text-base px-6 py-3 min-h-[48px]",
  };

  return (
    <motion.button
      whileHover={{ scale: 1.02, y: -1 }}
      whileTap={{ scale: 0.98 }}
      className={cn(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        fullWidth ? "w-full" : "",
        className,
      )}
      disabled={disabled || loading}
      {...(props as any)}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <svg
            className="animate-spin h-4 w-4 text-current"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </span>
      )}

      <span className={cn("flex items-center", loading ? "opacity-0" : "")}>
        {icon && <span className="mr-2">{icon}</span>}
        {children}
      </span>
    </motion.button>
  );
};

export default Button;
